/* To avoid server from trying to access DOM element in component */
import { ForwardRefExoticComponent, RefAttributes } from 'react';
import dynamic from 'next/dynamic';

import { AddressAutofillProps } from '@mapbox/search-js-react/dist/components/AddressAutofill';
import { AddressAutofillOptions } from '@mapbox/search-js-core';
import { AddressAutofillSuggestion } from '@mapbox/search-js-core/dist/autofill/types';
import { useTheme } from '@dh/hooks';

/* To avoid server from trying to access DOM element in component */
const InternalAddressAutofill = dynamic(
    () =>
        import('@mapbox/search-js-react').then((module) => module.AddressAutofill) as Promise<
            ForwardRefExoticComponent<AddressAutofillProps & RefAttributes<unknown>>
        >,
    { ssr: false }
);

// Bounding box for Schleswig-Holstein: [minLng, minLat, maxLng, maxLat]
// Approximate coordinates covering Schleswig-Holstein
const SchleswigHolsteinBBox: [number, number, number, number] = [
    7.8, // West (min longitude)
    53.3, // South (min latitude)
    11.5, // East (max longitude)
    55.1 // North (max latitude)
];

const SchleswigHolsteinOnly: Partial<AddressAutofillOptions> = {
    language: 'de',
    country: 'DE',
    bbox: SchleswigHolsteinBBox
};

export const isDesiredRegion = (
    properties: Omit<AddressAutofillSuggestion, 'action' | 'original_search_text'>,
    iso_3166_2: string = 'DE-SH'
) => {
    // when we have iso_3166_2 in metadata, we allow the desired region only
    return !('iso_3166_2' in properties.metadata) || properties.metadata?.iso_3166_2 === iso_3166_2;
};

const MapBoxToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? '';

export const DHAddressAutofill = ({
    options,
    ...restProps
}: Omit<AddressAutofillProps, 'accessToken' | 'theme'>) => {
    const { colors } = useTheme();
    return (
        <InternalAddressAutofill
            {...restProps}
            theme={{ variables: { colorPrimary: colors.primary } }}
            accessToken={MapBoxToken}
            options={{ ...SchleswigHolsteinOnly, ...options }}
        />
    );
};
